
import { Component, Vue } from 'vue-property-decorator';
import { Getter, Action } from 'vuex-class';
import { PERMISSION } from '@/permissions';
import { valueSort } from '../utils/sort';
import { cloneDeep, isEqual, isNil } from 'lodash';
import omitBy from 'lodash/omitBy';
import Toast from '@/utils/toast';
import ListItem from '@/components/shared/ListItem.vue';
import ModalWrapper from '@/components/shared/ModalWrapper.vue';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import AddOnOptionGroupEditor from '@/components/add-ons/AddOnOptionGroupEditor.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';

const namespace: string = 'options';

@Component<AddOnsList>({
	components: {
		ListItem,
		ModalWrapper,
		StandardModal,
		AddOnOptionGroupEditor,
		TextButton
	}
})
export default class AddOnsList extends Vue {
	@Action('fetchAddOnOptionGroups', { namespace }) private fetchAddOnOptionGroups!: () => Promise<Option[]>;
	@Action('deleteOptionGroup', { namespace }) private deleteOptionGroup!: (item: OptionGroup) => Promise<void>;
	@Action('setSelectedAddOnOptionGroup', { namespace }) private setSelectedAddOnOptionGroup!: (item: OptionGroup | object) => Promise<void>;
	@Getter('getAddOnOptionGroups', { namespace }) private addOnOptionGroups!: OptionGroup[];
	@Getter('getSelectedAddOnOptionGroup', { namespace }) private selectedAddOnOptionGroup!: OptionGroup;
	@Getter('isPosIntegrated', { namespace: 'auth' }) private isPosIntegrated!: boolean;
	@Getter('hasPermission', { namespace: 'auth' }) private hasPermission!: (permission: PERMISSION) => boolean;

	private PERMISSION = PERMISSION;
	private fetchingAddons: boolean = false;
	private hasFetched: boolean = false;
	private showDialog: boolean = false;
	private isCreating: boolean = false;
	private showDeleteModal: boolean = false;
	private showConfirmCloseModal: boolean = false;
	private loading: boolean = false;
	private addOnsEdited: boolean = false;
	private itemToDelete: object | OptionGroup = {};
	private selectedLocale: RestaurantLocale|null = null;

	private selectedAddOnOptionGroupBeforeEditSnapshot: any | OptionGroup = {};
	private addOnsBeforeEditSnapshot: Option[] = [];

	private actionsArray: object[] = [
		{
			iconName: 'mdi-pencil',
			buttonLabel: this.$t('shared.actions_array.btn_edit'),
			onClick: (evt: object, item: OptionGroup): void => {
				this.openAddOnEditor(false, item);
				this.setSelectedAddOnOptionGroup(item);
			}
		},
		{
			iconName: 'mdi-delete',
			buttonLabel: this.$t('shared.actions_array.btn_delete'),
			onClick: (evt: object, item: OptionGroup): void => {
				this.setItemToDeleteName(item);
			},
			permission: PERMISSION.MODIFIER_GROUP_DELETE
		},
		{
			iconName: 'mdi-window-close',
			buttonLabel: this.$t('shared.actions_array.btn_cancel'),
			onClick: null
		}
	];
	/**
	 * Checks for added, removed or edited addons and sets the respective flag if anything has changed.
	 *
	 * @param {Option[]} addOns
	 * @return {void}
	 */
	private setAddOnsEdited (addOns: Option[]): void {
		this.addOnsEdited = !isEqual(addOns, this.addOnsBeforeEditSnapshot);
	}

	/**
	 * Open Create Add On Modal
	 * @return {void}
	 */
	private openAddOnEditor (creating: boolean, item: OptionGroup) {
		this.showDialog = true;
		this.isCreating = creating;

		if (!this.isCreating) {
			this.selectedAddOnOptionGroupBeforeEditSnapshot = cloneDeep(item);
			this.addOnsBeforeEditSnapshot = cloneDeep(item.values as Option[]);
		}

		document.documentElement.classList.add('modal-open');
	}

	/**
	 * Fetch the option groups
	 *
	 * @return {void}
	 */
	private async created(): Promise<void> {
		this.fetchAddons();
	}

	/**
	 * Set the item name for the deletion modal
	 *
	 * @return {void}
	 */
	private setItemToDeleteName(item: OptionGroup): void {
		this.showDeleteModal = true;
		this.itemToDelete = item;
	}

	/**
	 * Cancel closing of modal through the confirmation close modal
	 *
	 * @return {void}
	 */
	private cancelCloseModal(): void {
		this.showConfirmCloseModal = false;
	}

	/**
	 * Check if needs to show the confirm close modal to the user
	 *
	 * @return {void}
	 */
	private confirmClose(saved: boolean = false) {
		const selectedOptionGroupToCompare = omitBy(this.selectedAddOnOptionGroup, isNil);
		const selectedOptionGroupBeforeEditSnapshotToCompare = omitBy(this.selectedAddOnOptionGroupBeforeEditSnapshot, isNil);
		this.selectedLocale = null;

		if (selectedOptionGroupBeforeEditSnapshotToCompare && selectedOptionGroupBeforeEditSnapshotToCompare.values && selectedOptionGroupBeforeEditSnapshotToCompare.values.length) {
			this.selectedAddOnOptionGroupBeforeEditSnapshot.values = this.selectedAddOnOptionGroupBeforeEditSnapshot.values.sort((a: Option, b: Option) => valueSort(a, b, 'price'));
		}

		if ((saved || (isEqual(selectedOptionGroupToCompare, selectedOptionGroupBeforeEditSnapshotToCompare)) && !this.addOnsEdited)) {
			this.close(saved);
		}
		else {
			this.showConfirmCloseModal = true;
		}
	}

	/**
	 * Close the AddOnOptionGroupEditor modal
	 *
	 * @return {void}
	 */
	private async close(saved: boolean): Promise<void> {
		this.showConfirmCloseModal = false;
		this.showDialog = false;
		this.isCreating = false;
		this.addOnsEdited = false;

		// Remove the selected option from the store
		this.setSelectedAddOnOptionGroup({});
		this.selectedAddOnOptionGroupBeforeEditSnapshot = {};
		this.addOnsBeforeEditSnapshot = [];
		document.documentElement.classList.remove('modal-open');

		// Re-fetch the option groups if the modal was simply closed
		if (!saved) {
			this.fetchAddons();
		}
	}

	private async fetchAddons(): Promise<void> {
		try {
			this.$emit('fetching-addons', true);
			this.fetchingAddons = true;
			await this.fetchAddOnOptionGroups();
		}
		catch (errorMessage) {
			Toast.error(this.$t('menu.addon_groups.error_fetching', { errorMessage }));
		}
		finally {
			this.$emit('fetching-addons', false);
			this.fetchingAddons = false;
			this.hasFetched = true;
		}
	}

	private cancel(): void {
		this.showDeleteModal = false;
		this.itemToDelete = {};
	}

	/**
	 * User confirmed deletion of the item, so we delete it
	 * and close the modal
	 *
	 * @return {Promise<void>}
	 */
	private async confirm(): Promise<void> {
		this.loading = true;
		await this.deleteOptionGroup(this.itemToDelete as OptionGroup)
			.catch((errorMessage) => {
				Toast.error(this.$t('shared.error_deleting', { name: (this.itemToDelete as OptionGroup).name, errorMessage }));
			});
		this.itemToDelete = {};
		this.loading = false;
		this.showDeleteModal = false;
	}
}
